// @ts-nocheck -- Legacy JavaScript compatibility boundary; runtime behavior intentionally preserved.
import { useState } from "react";
import PropTypes from "prop-types";
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
  GridColumnHeaderTitle,
} from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import RefreshIcon from "@mui/icons-material/Refresh";
import TextField from "@mui/material/TextField";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import { useDynamicTranslation } from "@/hooks/useDynamicTranslation";
import { Dialog } from "../dialog";

export function DefaultToolbar({
  searchBoxProps,
  setFilterButtonEl,
  hideFilterButton,
  hideExportButton,
  getRows,
}) {
  const { t } = useDynamicTranslation("shared");

  return (
    <GridToolbarContainer sx={{ display: "flex", justifyContent: "space-between" }}>
      <Box>
        <Button startIcon={<RefreshIcon />} onClick={() => getRows()}>
          {t("refresh")}
        </Button>
        <GridToolbarColumnsButton ref={setFilterButtonEl} />
        {!hideFilterButton && <GridToolbarFilterButton ref={setFilterButtonEl} />}
        <GridToolbarDensitySelector />
        {!hideExportButton && <GridToolbarExport />}
      </Box>
      {searchBoxProps && (
        <Box>
          <TextField
            variant="standard"
            value={searchBoxProps.value}
            onChange={searchBoxProps.onChange}
            placeholder={t("search")}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" />,
              endAdornment: (
                <IconButton
                  title="Clear"
                  aria-label="Clear"
                  size="small"
                  style={{
                    visibility: searchBoxProps.value ? "visible" : "hidden",
                  }}
                  onClick={searchBoxProps.clearSearch}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ),
            }}
            sx={{
              width: {
                xs: 1,
                sm: "auto",
              },
              m: (theme) => theme.spacing(1, 0.5, 1.5),
              "& .MuiSvgIcon-root": {
                mr: 0.5,
              },
              "& .MuiInput-underline:before": {
                borderBottom: 1,
                borderColor: "divider",
              },
            }}
          />
        </Box>
      )}
    </GridToolbarContainer>
  );
}

export function UPTToolbar({
  searchBoxProps,
  hideFilterButton,
  FilterForm,
  dataTableTitle,
  tableFilters,
  setTableFilters,
  loadData,
}) {
  const { t } = useDynamicTranslation("shared");
  const theme = useTheme();
  const [filterDialog, setFilterDialog] = useState(false);

  return (
    <GridToolbarContainer>
      <Box sx={{ display: "flex", alignItems: "space-between", width: "100%", mt: 1 }}>
        {searchBoxProps && (
          <Box sx={theme.dataGrid.upt.toolbar.box}>
            <TextField
              variant="standard"
              value={searchBoxProps.value}
              onChange={searchBoxProps.onChange}
              placeholder={t("search")}
              fullWidth
              InputProps={{
                disableUnderline: true,
              }}
              sx={{ height: "auto", m: 3 }}
            />
          </Box>
        )}
        {!searchBoxProps && dataTableTitle && (
          <Typography variant="h5" fontWeight="bold" component="div">
            <GridColumnHeaderTitle label={dataTableTitle.toString()} columnWidth={0} />
          </Typography>
        )}
        {!hideFilterButton && (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={async () => {
                await loadData({ index: 1, size: 10 });
                setTableFilters();
              }}
              variant="contained"
              color="inherit"
            >
              {t("form.reset")}
            </Button>
            <Button sx={{ mx: 1 }} onClick={() => setFilterDialog(true)} variant="contained">
              {t("filter")}
            </Button>
            <Dialog
              onClose={() => setFilterDialog(false)}
              open={filterDialog}
              sx={{
                "& .MuiBox-root": {
                  justifyContent: "start",
                },
              }}
            >
              <FilterForm onClose={() => setFilterDialog(false)} />
            </Dialog>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
        }}
      >
        {tableFilters &&
          Object.entries(tableFilters).map(([key, value]) => {
            if (typeof value === "object" && Object.values(value).every((val) => val !== "")) {
              return (
                <Box key={key} sx={{ display: "flex" }}>
                  <Typography sx={{ fontWeight: "bold" }}>{t(`form.${key}`)} : </Typography>
                  {Object.entries(value).map(([subKey, subValue], index) => (
                    <Typography key={subKey}>
                      {index % 2 === 1 && " - "}
                      {key === "manuelTimeRange" && subValue
                        ? dayjs(subValue).format("DD.MM.YYYY")
                        : subValue}
                    </Typography>
                  ))}
                </Box>
              );
            }

            if (typeof value !== "object" && value !== "") {
              return (
                <Box key={key} sx={{ display: "flex" }}>
                  <Typography sx={{ fontWeight: "bold" }}>{t(`form.${key}`)} : </Typography>
                  <Typography>
                    {key === "timeRange"
                      ? timeRange[value]
                      : key === "transactionType"
                        ? transactionType[value]
                        : value}
                  </Typography>
                </Box>
              );
            }

            return null;
          })}
      </Box>
    </GridToolbarContainer>
  );
}

const timeRange = {
  0: "Bugün",
  1: "Bu Hafta",
  2: "Bu Ay",
  3: "Bu Yıl",
};

const transactionType = {
  0: "Tüm İşlemler",
  1: "Para Çıkışı",
  2: "Para Girişi",
};

DefaultToolbar.propTypes = {
  searchBoxProps: PropTypes.shape({
    clearSearch: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
  }),
  setFilterButtonEl: PropTypes.func.isRequired,
  hideFilterButton: PropTypes.bool,
  hideExportButton: PropTypes.bool,
  getRows: PropTypes.func.isRequired,
};

UPTToolbar.propTypes = {
  searchBoxProps: PropTypes.shape({
    clearSearch: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
  }),
  hideFilterButton: PropTypes.bool,
  FilterForm: PropTypes.any,
  dataTableTitle: PropTypes.string,
  tableFilters: PropTypes.any,
  setTableFilters: PropTypes.any,
  loadData: PropTypes.any,
};
